/**
 * Color Sibling Switcher — AJAX-based product switching
 *
 * Key: Uses the template-scoped section ID (e.g. template--XXXXX__main)
 * instead of the bare section type name, so that Shopify returns the
 * section with all its block configuration (title, price, variant picker, etc.)
 */
(function () {
  'use strict';

  var SECTION_TYPE_SELECTOR = '[data-section-type="main-product"]';
  var SWATCH_SELECTOR = '[data-color-siblings] .color-siblings__swatch';
  var LOADING_CLASS = 'color-switcher--loading';
  var FETCH_TIMEOUT = 10000;

  var isSwapping = false;
  var currentRequest = null;

  /**
   * Get the real section ID from the page's Shopify section wrapper.
   * For JSON templates this is like "template--27890348491126__main",
   * NOT just "main-product".
   */
  function getSectionId() {
    var sectionTypeEl = document.querySelector(SECTION_TYPE_SELECTOR);
    if (!sectionTypeEl) return 'main-product';

    var wrapper = sectionTypeEl.closest('.shopify-section');
    if (!wrapper || !wrapper.id) return 'main-product';

    // wrapper.id is "shopify-section-template--XXXXX__main" — strip the prefix
    return wrapper.id.replace('shopify-section-', '');
  }

  function init() {
    bindSwatchClicks();
    bindPopState();
  }

  function bindSwatchClicks() {
    document.addEventListener('click', function (e) {
      var swatch = e.target.closest(SWATCH_SELECTOR);
      if (!swatch) return;

      e.preventDefault();
      if (swatch.classList.contains('color-siblings__swatch--active')) return;
      if (isSwapping) return;

      var handle = swatch.getAttribute('data-product-handle');
      var url = swatch.getAttribute('data-product-url');
      if (!handle || !url) return;

      switchProduct(handle, url, true);
    });
  }

  function bindPopState() {
    window.addEventListener('popstate', function (e) {
      if (e.state && e.state.colorSwitcher && e.state.handle) {
        switchProduct(e.state.handle, e.state.url, false);
      }
    });

    if (document.querySelector('[data-color-siblings]')) {
      var currentHandle = getCurrentHandle();
      if (currentHandle) {
        history.replaceState(
          { colorSwitcher: true, handle: currentHandle, url: window.location.pathname },
          document.title,
          window.location.href
        );
      }
    }
  }

  function getCurrentHandle() {
    var match = window.location.pathname.match(/\/products\/([^/?#]+)/);
    return match ? match[1] : null;
  }

  function switchProduct(handle, url, pushState) {
    if (isSwapping) return;
    isSwapping = true;

    if (currentRequest) {
      currentRequest.abort();
      currentRequest = null;
    }

    var sectionTypeEl = document.querySelector(SECTION_TYPE_SELECTOR);
    if (sectionTypeEl) {
      sectionTypeEl.classList.add(LOADING_CLASS);
    }

    // Use the template-scoped section ID so Shopify includes all block config
    var sectionId = getSectionId();
    var fetchUrl = '/products/' + handle + '?section_id=' + sectionId;

    var xhr = new XMLHttpRequest();
    currentRequest = xhr;

    var timeoutId = setTimeout(function () {
      xhr.abort();
      handleError(url);
    }, FETCH_TIMEOUT);

    xhr.open('GET', fetchUrl, true);
    xhr.onload = function () {
      clearTimeout(timeoutId);
      currentRequest = null;

      if (xhr.status >= 200 && xhr.status < 300) {
        performSwap(xhr.responseText, handle, url, pushState);
      } else {
        handleError(url);
      }
    };

    xhr.onerror = function () {
      clearTimeout(timeoutId);
      currentRequest = null;
      handleError(url);
    };

    xhr.onabort = function () {
      clearTimeout(timeoutId);
      currentRequest = null;
    };

    xhr.send();
  }

  function performSwap(html, handle, url, pushState) {
    try {
      // 1. Parse the response into a real DOM
      var doc = new DOMParser().parseFromString(html, 'text/html');

      // 2. Find the new section-type element in the parsed response
      var newSectionTypeEl = doc.querySelector(SECTION_TYPE_SELECTOR);
      if (!newSectionTypeEl) {
        throw new Error('New section element not found in response');
      }

      // 3. Find the old section-type element on the page
      var oldSectionTypeEl = document.querySelector(SECTION_TYPE_SELECTOR);
      if (!oldSectionTypeEl) {
        throw new Error('Old section element not found on page');
      }

      // 4. Destroy existing JS instances on the OLD element
      destroyExistingInstances(oldSectionTypeEl);

      // 5. Remove animation attributes so content is immediately visible
      newSectionTypeEl.querySelectorAll('[data-cc-animate]').forEach(function (el) {
        el.removeAttribute('data-cc-animate');
        el.removeAttribute('data-cc-animate-delay');
        el.removeAttribute('data-cc-animate-duration');
      });

      // 6. Import and replace
      var importedNode = document.importNode(newSectionTypeEl, true);
      oldSectionTypeEl.replaceWith(importedNode);

      // 7. Re-execute inline scripts (needed for B2B bulk order widget, etc.)
      rerunInlineScripts(importedNode);

      // 8. Re-initialize all JS behaviors
      reinitialize(importedNode);

      // 9. Update browser URL
      if (pushState) {
        var newUrl = url || '/products/' + handle;
        history.pushState(
          { colorSwitcher: true, handle: handle, url: newUrl },
          '',
          newUrl
        );
      }

      // 10. Update page title
      updatePageTitle(importedNode);

      // 11. Scroll to product on mobile
      scrollToProduct(importedNode);

    } catch (err) {
      console.error('Color Switcher: swap error', err);
      window.location.href = url || '/products/' + handle;
      return;
    } finally {
      isSwapping = false;
    }
  }

  /**
   * Re-execute inline <script> tags inside swapped content.
   * Scripts inserted via importNode/innerHTML don't run automatically,
   * so we clone each one into a fresh <script> element.
   */
  function rerunInlineScripts(container) {
    var scripts = container.querySelectorAll('script');
    scripts.forEach(function (oldScript) {
      var newScript = document.createElement('script');
      if (oldScript.src) {
        // External script — skip, it should already be loaded
        return;
      }
      newScript.textContent = oldScript.textContent;
      // Copy attributes (type, etc.)
      Array.from(oldScript.attributes).forEach(function (attr) {
        newScript.setAttribute(attr.name, attr.value);
      });
      oldScript.parentNode.replaceChild(newScript, oldScript);
    });
  }

  function destroyExistingInstances(container) {
    try {
      var $container = $(container);

      $container.find('.product-form').trigger('unload-product-form');

      if (typeof theme !== 'undefined' && typeof theme.destroyProductGallery === 'function') {
        theme.destroyProductGallery(container);
      }

      $container.find('.slick-initialized').each(function () {
        try { $(this).slick('unslick'); } catch (e) {}
      });

      if (typeof theme !== 'undefined' && theme.ProductBlockManager) {
        theme.ProductBlockManager.unloadImages(container);
      }

      $container.find('.theme-product-reviews').off('click');

      // Unbind B2B bulk order widget events
      $container.find('.add-to-cart-order-form').off('click');
      $container.find('.quantity__button').off('click');
      $container.find('.quantity__input').off('change');
      $container.find('.dd-variant-to-cart').off('click');
    } catch (err) {
      console.warn('Color Switcher: error destroying instances', err);
    }
  }

  function reinitialize(container) {
    try {
      var $container = $(container);

      // Theme components (accordion, custom-select, modal) — must run before
      // product form init so that custom selects are styled with the theme font
      var componentAttr = container.getAttribute('data-components');
      if (componentAttr) {
        componentAttr.split(',').forEach(function (component) {
          $(document).trigger('cc:component:load', [component.trim(), container]);
        });
      }

      // Gallery first (so it can respond to variant images)
      if (typeof theme !== 'undefined' && typeof theme.initProductGallery === 'function') {
        theme.initProductGallery(container);
      }

      // Product form (variant picker, AJAX cart, variant change handlers)
      $container.find('.product-form').trigger('load-product-form');

      // Product block images
      if (typeof theme !== 'undefined' && theme.ProductBlockManager) {
        theme.ProductBlockManager.loadImages(container);
      }

      // Store availability
      var storeAvailEl = container.querySelector('[data-store-availability-container]');
      if (storeAvailEl && typeof theme !== 'undefined' && typeof theme.StoreAvailability === 'function') {
        new theme.StoreAvailability(storeAvailEl);
      }

      // Review clicks
      $container.find('.theme-product-reviews').on('click', 'a', function () {
        $('html,body').animate({ scrollTop: $($(this).attr('href')).offset().top }, 1000);
        return false;
      });

      // Lazy images
      if (typeof lazySizes !== 'undefined') {
        lazySizes.init();
      }

      // Scroll animations
      if (typeof theme !== 'undefined' && typeof theme.initAnimateOnScroll === 'function') {
        theme.initAnimateOnScroll();
      }

      // Dynamic Payment Buttons
      if (typeof Shopify !== 'undefined' && Shopify.PaymentButton) {
        Shopify.PaymentButton.init();
      }

      // Klaviyo
      if (typeof klaviyo !== 'undefined' && typeof klaviyo.enable === 'function') {
        try { klaviyo.enable('backinstock'); } catch (e) {}
      }
    } catch (err) {
      console.error('Color Switcher: reinit error', err);
    }
  }

  function updatePageTitle(container) {
    var titleEl = container.querySelector('h1.title');
    if (titleEl) {
      var productTitle = titleEl.textContent.trim();
      var shopName = '';
      var titleParts = document.title.split(' \u2013 ');
      if (titleParts.length <= 1) {
        titleParts = document.title.split(' – ');
      }
      if (titleParts.length > 1) {
        shopName = titleParts[titleParts.length - 1];
      }
      document.title = shopName ? productTitle + ' \u2013 ' + shopName : productTitle;
    }
  }

  function scrollToProduct(container) {
    if (window.innerWidth < 768) {
      var rect = container.getBoundingClientRect();
      if (rect.top < 0 || rect.top > window.innerHeight * 0.5) {
        window.scrollTo({ top: container.offsetTop - 80, behavior: 'smooth' });
      }
    }
  }

  function handleError(url) {
    isSwapping = false;
    var el = document.querySelector(SECTION_TYPE_SELECTOR);
    if (el) el.classList.remove(LOADING_CLASS);
    if (url) window.location.href = url;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
